/**
 * Layout 1 -- the tree table: one row per node, owners and counts in columns,
 * rows that expand to the level beneath. A name links to the node's own
 * screen; the rest of the row opens the detail panel.
 */

import { useMemo, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';

import { ContextTag, HolderChip, RiskBar, RiskPill, StatusBadge } from './OverviewParts';
import { detailPath, expandableIds, type OrgNode } from './overviewModel';
import type { ViewProps } from './viewProps';

interface Row {
  node: OrgNode;
  depth: number;
}

export function TreeTableView({ roots, tree, selectedId, onSelect, filtering }: ViewProps) {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => new Set(roots[0] ? [roots[0].id] : []),
  );
  const allIds = useMemo(() => expandableIds(roots), [roots]);

  const isOpen = (id: string) => filtering || expanded.has(id);
  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const rows = useMemo(() => {
    const out: Row[] = [];
    const visit = (node: OrgNode, depth: number) => {
      out.push({ node, depth });
      if (node.children.length > 0 && (filtering || expanded.has(node.id))) {
        node.children.forEach((child) => visit(child, depth + 1));
      }
    };
    roots.forEach((root) => visit(root, 0));
    return out;
  }, [roots, expanded, filtering]);

  return (
    <div className="card">
      <div className="ov-tools">
        <h2>All groups</h2>
        <div className="ov-tools-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExpanded(new Set(allIds))}>
            Expand all
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setExpanded(new Set())}>
            Collapse all
          </button>
        </div>
      </div>
      <div className="table-scroll">
        <div className="ov-tt" role="treegrid" aria-label="Organization">
          <div className="ov-tt-row ov-tt-head" role="row">
            <span role="columnheader">Name</span>
            <span role="columnheader">Delivery manager</span>
            <span role="columnheader">Sales manager</span>
            <span role="columnheader" className="num">Clients</span>
            <span role="columnheader" className="num">Engagements</span>
            <span role="columnheader">Risk mix</span>
          </div>
          {rows.map(({ node, depth }) => {
            const expandable = node.children.length > 0;
            const target = detailPath(node, tree.byId);
            const onKey = (event: KeyboardEvent) => {
              if (event.target !== event.currentTarget) return;
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect(node.id);
              }
            };
            return (
              <div
                key={node.id}
                role="row"
                aria-level={depth + 1}
                aria-expanded={expandable ? isOpen(node.id) : undefined}
                aria-selected={selectedId === node.id}
                tabIndex={0}
                className={`ov-tt-row l${depth}${selectedId === node.id ? ' is-selected' : ''}`}
                onClick={() => onSelect(node.id)}
                onKeyDown={onKey}
              >
                <span className="nm" role="gridcell" style={{ paddingLeft: `${depth * 22}px` }}>
                  <button
                    type="button"
                    className={`ov-tog${expandable ? '' : ' leaf'}`}
                    aria-label={`${isOpen(node.id) ? 'Collapse' : 'Expand'} ${node.name}`}
                    aria-expanded={expandable ? isOpen(node.id) : undefined}
                    tabIndex={expandable ? 0 : -1}
                    disabled={!expandable || filtering}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggle(node.id);
                    }}
                  >
                    <ChevronRight aria-hidden="true" className={isOpen(node.id) ? 'is-open' : ''} />
                  </button>
                  {target ? (
                    <Link className="cell-link t" to={target} onClick={(event) => event.stopPropagation()}>
                      {node.name}
                    </Link>
                  ) : (
                    <span className="t">{node.name}</span>
                  )}
                  <StatusBadge node={node} />
                  <ContextTag node={node} />
                </span>
                {node.level === 'engagement' ? (
                  <>
                    <span role="gridcell"><HolderChip holder={node.head} kind="head" /></span>
                    <span role="gridcell" className="hint">{node.services ?? ''}</span>
                    <span role="gridcell" className="num hint">—</span>
                    <span role="gridcell" className="num hint">—</span>
                    <span role="gridcell"><RiskPill riskLevel={node.riskLevel} /></span>
                  </>
                ) : (
                  <>
                    <span role="gridcell">
                      {node.inScope && <HolderChip holder={node.delivery} kind="delivery" />}
                    </span>
                    <span role="gridcell">
                      {node.inScope && <HolderChip holder={node.sales} kind="sales" />}
                    </span>
                    <span role="gridcell" className="num">{node.level === 'client' ? '—' : node.counts.clients}</span>
                    <span role="gridcell" className="num">{node.counts.engagements}</span>
                    <span role="gridcell"><RiskBar node={node} /></span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
